import { api } from '@/lib/api';
import { hasSessionHint, type MePayload } from '@/lib/auth';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export const ME_QUERY_KEY = ['me'] as const;

export function useAuth() {
  // Only probe GET /me for visitors who have had a session (the persisted
  // localStorage hint set on login / refresh-success). A cold anonymous visitor
  // has no token and no refresh cookie, so /me just 401s — skip it entirely and
  // resolve as not-authenticated with zero network. Login sets the hint, so a
  // signed-in user (incl. F5, where the in-memory token is gone) still fetches
  // /me and recovers via the refresh flow.
  const enabled = hasSessionHint();
  const query = useQuery<MePayload, AxiosError>({
    queryKey: ME_QUERY_KEY,
    queryFn: async () => {
      // Backend returns MeResponse flat — no `{data: ...}` envelope.
      const resp = await api.get<MePayload>('/me');
      return resp.data;
    },
    enabled,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return {
    user: query.data?.user ?? null,
    org: query.data?.org ?? null,
    quota: query.data?.quota ?? null,
    storage: query.data?.storage ?? null,
    memberships: query.data?.memberships ?? [],
    subscription: query.data?.subscription ?? null,
    isSuperuser: query.data?.user?.is_superuser ?? false,
    isAuthenticated: !!query.data && !query.isError,
    // A disabled query reports isLoading=false, so an anonymous visitor resolves
    // immediately (no spinner flash) instead of hanging on a fetch that never runs.
    isLoading: enabled && query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
  };
}
